import ElasticAdapter from '@/features/shared/Adapters/ElasticAdapter';
import ElasticComponentContext, {
  ElasticComponentContextType,
} from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { useBotSearchParams, useCMSSearchLocation } from '@/features/shared/Hooks';
import { useElasticSearch } from '@/features/shared/Hooks/useElasticSearch';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { colorMap, LoadingStatusValues } from '@/utils/constants';
import { readCsvLine } from '@/utils/helpers';
import {
  resolveBool,
  resolveColorName,
  resolveContentBlocks,
  resolveLink,
  resolveSortBy,
  resolveStatus,
  resolveString,
  resolveTaxonomyFacets,
  resolveTheme,
} from '@/utils/resolvers';
import { isEqual, union } from 'lodash/fp';
import qs from 'query-string';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import CMSWrapper from '../shared/CMSWrapper';
import { DEFAULT_ELASTIC_FIELDS } from '../shared/Hooks/elasticSearch/useElasticSearch.constants';
import { parseSortByPublicationDate } from '../shared/Hooks/elasticSearch/useElasticSearch.utils';
import FilteredList from './FilteredList';
import {
  mapTarget,
  onDatalayer,
  onDatalayerCustomFilters,
  onDatalayerCustomSearch,
  onDatalayerCustomSort,
  onDatalayerDownloadFile,
} from './FilteredList.datalayer';
import { elasticSearchAuthDefaultFields } from './FilteredList.utils';
import { useAnchorSearch } from './useAnchorSearch';
import { withGTMPipe } from '@/features/shared/HOCs/withGTM';

const resolveLinkArray = ({ rawVal, page, pageModels }: any) =>
  rawVal.map((val: LinkCompound) => resolveLink({ rawVal: val, page, pageModels }));

const MappedFilteredList = ({
  content,
  presentation,
  contentButtons,
  pageModels,
  isPreview,
  labels,
  sortBy: sortOptions = [],
  sortByExcluded: sortOptionsExcluded = {},
  categoryUrl = {},
  monthLabel,
  page: brPage,
  filteredListConfig = {},
  contentCategoryCardFieldMapper = {},
  dataLayerSearchAttributesByContentCategory = {},
}: any) => {
  const { config, esLabels, taxonomy: taxonomies = {} } = pageModels;
  const { elasticsearch: elasticsearchConfig } = config || {};
  const {
    search_key: searchKey,
    resources_engine: engine,
    resources_auth_engine: authEngine,
    engines_query_sorting_blacklist: sortingBlacklist,
  } = elasticsearchConfig || {};
  const { containerBackgroundColor = colorMap['dark-gray'], cardStyle, defaultSort } = presentation;
  const { contentCategoryKeys = [], taxonomyFacets, additionalFacets } = content;

  const facets: string[] = React.useMemo(
    () => [...(additionalFacets || []), ...(taxonomyFacets || [])],
    [additionalFacets, taxonomyFacets],
  );

  const contentCategory = React.useMemo(
    () => contentCategoryKeys?.map((taxonomyKey: string) => taxonomies?.[taxonomyKey]),
    [contentCategoryKeys, taxonomies],
  );
  const lastContentCategory = useRef(contentCategory);

  const EXISTS_CONTENT_CATEGORY = React.useMemo(
    () => contentCategory.length > 0,
    [contentCategory.length],
  );
  const isGrid = useMemo(() => /grid/.test(cardStyle), [cardStyle]);
  const theme = useMemo(
    () => resolveTheme('bg-color')({ rawVal: containerBackgroundColor }),
    [containerBackgroundColor],
  );
  const [ableToSearch, setAbleToSearch] = useState(false);
  const reactHistory = useHistory();
  const defaultParams = useRef({
    q: '',
    page: '1',
    sortBy: defaultSort || 'most-relevant',
    keywords: '',
    size: isGrid ? '9' : '10',
    filters: {},
  });
  const { language } = pageModels;
  const { isAuthenticated } = useIsAuthenticated();

  const { appPath: LOCATION, isSearch: IS_SEARCH } = useCMSSearchLocation(brPage);
  useBotSearchParams(IS_SEARCH);

  const {
    q = defaultParams.current.q,
    keywords = defaultParams.current.keywords,
    page = defaultParams.current.page,
    sortBy = defaultParams.current.sortBy,
    size = defaultParams.current.size,
    ...filters
  } = qs.parse(window.location.search);

  // update
  const [needsUpdate, setNeedsUpdate] = useState(true);

  // Search Params
  const [search, setSearch] = useState({
    q,
    page,
    sortBy,
    keywords,
    size,
    filters: ElasticAdapter.encodeFilters(
      EXISTS_CONTENT_CATEGORY
        ? {
            ...filters,
            tax_content_category: filters.tax_content_category || [],
          }
        : filters,
      facets,
    ),
  });

  const isSortShown = useMemo(
    () => !(readCsvLine({ data: sortingBlacklist }).includes(engine) && search?.q),
    [engine, sortingBlacklist, search?.q],
  );

  useEffect(() => {
    if (isEqual(contentCategory, lastContentCategory.current)) return;

    lastContentCategory.current = contentCategory;

    setAbleToSearch(false);
    setNeedsUpdate(true);

    if (!IS_SEARCH) {
      setSearch(defaultParams.current);
    } else {
      setSearch(() => {
        const params = qs.parse(window.location.search);

        return {
          q: params.q || defaultParams.current.q,
          page: params.page || defaultParams.current.page,
          sortBy: params.sortBy || defaultParams.current.sortBy,
          keywords: params.keywords || defaultParams.current.keywords,
          size: params.size || defaultParams.current.size,
          filters: ElasticAdapter.encodeFilters(
            EXISTS_CONTENT_CATEGORY
              ? {
                  ...filters,
                  tax_content_category: filters.tax_content_category || [],
                }
              : filters,
            facets,
          ),
        };
      });
    }
  }, [EXISTS_CONTENT_CATEGORY, IS_SEARCH, contentCategory, facets, filters, search]);

  const elasticSearchFields = React.useMemo(() => {
    const defaultFields = !isAuthenticated
      ? DEFAULT_ELASTIC_FIELDS
      : union(DEFAULT_ELASTIC_FIELDS, elasticSearchAuthDefaultFields);

    if (!filteredListConfig?.cards_hidden_date) return defaultFields;

    const contentCategoryKeysWithHiddenDates = readCsvLine({
      data: filteredListConfig?.cards_hidden_date,
    });
    const hasCardsHiddenDate = contentCategoryKeysWithHiddenDates?.some(
      (contentCategoryKeyWithHiddenDate) =>
        contentCategoryKeys?.includes(contentCategoryKeyWithHiddenDate),
    );

    if (!hasCardsHiddenDate) return defaultFields;

    return defaultFields.filter(
      (field = '') => !['publication_date', 'release_date'].includes(field),
    );
  }, [contentCategoryKeys, filteredListConfig?.cards_hidden_date, isAuthenticated]);

  const es = useElasticSearch({
    engine,
    authEngine,
    searchParams: search,
    useDynamicFacets: false,
    facets,
    fields: elasticSearchFields,
    initBodyFilters: EXISTS_CONTENT_CATEGORY ? ['tax_content_category'] : [],
    searchKey,
    needsUpdate, // it disables watch on search state
    parseSortBy: parseSortByPublicationDate,
    filtersFallback: EXISTS_CONTENT_CATEGORY ? { tax_content_category: contentCategory } : {},
    language,
    labels: esLabels,
  });

  // Load Facets on first render
  useEffect(() => {
    es.reloadFacets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facets]);

  const forceUpdate = () => {
    if (!IS_SEARCH) {
      reactHistory.push(`${LOCATION}search?${qs.stringify(ElasticAdapter.sanitizeParams(search))}`);
    }

    setNeedsUpdate(true);
  };

  const setQueryParam = (key: string, value: any, isFilter?: boolean): void => {
    if (isFilter) {
      setSearch((data: any) => ({
        ...data,
        filters: {
          ...data.filters,
          [key]: value,
        },
      }));
    } else {
      setSearch((data: any) => ({
        ...data,
        [key]: value,
      }));
      forceUpdate();
    }
  };

  React.useEffect(() => {
    if (isSortShown) return;

    setQueryParam('sortBy', defaultParams.current.sortBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSortShown]);

  const resetParams = () => {
    setSearch(defaultParams.current);
    forceUpdate();
  };

  useEffect(() => {
    if (!isPreview && needsUpdate && ableToSearch) {
      // eslint-disable-next-line no-restricted-globals
      window.history.pushState({}, '', `?${qs.stringify(ElasticAdapter.sanitizeParams(search))}`);
    }
    setAbleToSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreview, needsUpdate]);

  useEffect(() => {
    if (!es.isLoading) {
      setNeedsUpdate(false);
    }
  }, [es.isLoading]);

  useAnchorSearch({
    defaultParams: defaultParams.current,
    setSearch,
    needsUpdate,
    qparams: {
      q,
      page,
      sortBy,
      keywords,
      size,
      filters: ElasticAdapter.encodeFilters(
        EXISTS_CONTENT_CATEGORY
          ? {
              ...filters,
              tax_content_category: [],
            }
          : filters,
        facets,
      ),
    },
  });

  const allLabels = React.useMemo(
    () => ({
      ...labels,
      monthLabel,
      ...taxonomies,
    }),
    [labels, monthLabel, taxonomies],
  );

  const { loading } = LoadingStatusValues;
  const [status, setStatus] = useState<ElasticComponentContextType['status']>(loading);
  useEffect(() => resolveStatus({ rawVal: es, setStatus, isSearch: IS_SEARCH }), [es, IS_SEARCH]);
  const filteredListContent = useMemo(
    () => ({ ...content, isCached: es.isCached }),
    [content, es.isCached],
  );
  const filteredListPresentation = useMemo(
    () => ({ ...presentation, isSortShown }),
    [presentation, isSortShown],
  );

  const sortOptionsFiltered = React.useMemo(() => {
    if (!contentCategory?.length) return sortOptions;
    // Excluding pages that contains multiple content types.
    if (contentCategory?.length > 1) return sortOptions;

    const hasExclusion = contentCategory?.some((type = '') => sortOptionsExcluded?.[type]);
    if (!hasExclusion) return sortOptions;

    const optionsExcluded = contentCategory.reduce((acc: string[], curr = '') => {
      if (!sortOptionsExcluded?.[curr]) return [...acc];

      const options = readCsvLine({ data: sortOptionsExcluded?.[curr] });
      return [...acc, ...options];
    }, []);

    return sortOptions?.filter(({ key = '' }) => !optionsExcluded.includes(key));
  }, [contentCategory, sortOptions, sortOptionsExcluded]);

  return (
    <div
      data-datalayer-position="filtered_list"
      data-datalayer-variant={presentation?.datalayerVariant}
    >
      <ElasticComponentContext.Provider
        value={{
          queryParams: search,
          updateQueryParams: setQueryParam,
          forceUpdate,
          sortOptions: sortOptionsFiltered,
          pagination: es.response.pagination,
          facets: es.facets,
          resetParams,
          // eslint-disable-next-line no-nested-ternary
          status,
          results: es?.response?.results || [],
          labels: allLabels,
          manualUpdate: true,
          engine,
          requestID: es.response?.meta?.request_id,
          searchKey,
        }}
      >
        <FilteredList
          content={filteredListContent}
          presentation={filteredListPresentation}
          contentButtons={contentButtons}
          theme={theme}
          labels={allLabels}
          categoryUrl={categoryUrl}
          isSearchPage={IS_SEARCH}
          contentCategoryCardFieldMapper={contentCategoryCardFieldMapper}
          contentCategoryKey={contentCategoryKeys?.[0] || ''}
          dataLayerSearchAttributesByContentCategory={dataLayerSearchAttributesByContentCategory}
        />
      </ElasticComponentContext.Provider>
    </div>
  );
};

const MappedFilteredListGTM = withGTMPipe(
  {
    onDatalayer,
    config: { mapTarget },
  },
  {
    onDatalayer: onDatalayerCustomSort,
    config: {
      eventName: 'gtm:custom:sort',
      isCustom: true,
    },
  },
  {
    onDatalayer: onDatalayerCustomSearch,
    config: {
      eventName: 'gtm:custom:search',
      isCustom: true,
    },
  },
  {
    onDatalayer: onDatalayerCustomFilters,
    config: {
      eventName: 'gtm:custom:filters',
      isCustom: true,
    },
  },
  {
    onDatalayer: onDatalayerDownloadFile,
    config: {
      eventName: 'gtm:custom:download-file',
      isCustom: true,
    },
  },
)(MappedFilteredList);

const FilteredListCMSWrapper = (brProps: any) => {
  const mapping = useRef({
    document: [
      'content',
      {
        listTitle: null,
        listMenuLink: [null, resolveLinkArray],
        editorialContent: ['featuredContent', resolveContentBlocks],
        // Taxonomy integration.
        contentcategory: 'contentCategoryKeys',
        taxonomyTopLevel: ['taxonomyFacets', resolveTaxonomyFacets],
        additionalfilters: 'additionalFacets',
      },
    ],

    'filteredListComponent.labels': 'labels',
    'filteredListComponent.config': 'filteredListConfig',
    componentParameterMap: [
      'presentation',
      {
        cardStyle: null,
        includeBreadcrumb: [null, resolveBool],
        'container-background-color': ['containerBackgroundColor', resolveColorName],
        'header-background-color': ['headerBackgroundColor', resolveColorName],
        includeCardImages: [null, resolveBool],
        jumplink: [null, resolveBool],
        searchHint: [null, resolveString],
        searchTip: [null, resolveString],
        defaultSort: [null, resolveString],
        'variant-label': 'datalayerVariant',
      },
    ],
    'value-lists/filtered-list-sort-by': ['sortBy', resolveSortBy],
    'value-lists/filtered-list-sort-by-excluded': 'sortByExcluded',
    'value-lists/filtered-list-category-url': 'categoryUrl',
    'value-lists/filtered-list-months': 'monthLabel',
    'taxonomies---content-category-card-field-mapper': 'contentCategoryCardFieldMapper',
    'filtered-list---datalayer-search-attributes-by-content-category':
      'dataLayerSearchAttributesByContentCategory',
  });

  const requiredFields = useRef({
    document: '<b>Filtering & Editorial Content</b> required',
  });

  const contentButtonNames = useRef({
    document: 'content',
  });

  return (
    <CMSWrapper
      brProps={brProps}
      render={MappedFilteredListGTM}
      mapping={mapping.current}
      requiredFields={requiredFields.current}
      contentButtonNames={contentButtonNames.current}
    />
  );
};

export default FilteredListCMSWrapper;
